import { Box, Tooltip } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { getRequest, postRequest } from "../../Apis";
import { useNavigate, useParams } from "react-router-dom";
import { campaignEndpoints } from "../../utils/constants/httpConstants";
import { CircularLoader } from "../../components";
import {
  Logger,
  capitalizeFirstLetter,
  debounceHelper,
  generateNameFromEmail,
  processStatsKeyValuePairs,
} from "../../utils/helpers";
import {
  CONTACTS_STATS_TYPE,
  DEBOUNCE_DELAY,
  EMITTER_ERROR,
  EMITTER_SUCCESS,
} from "../../utils/constants";
import {
  BRAND,
  GREY,
  INTER_DIMENSIONAL_BLUE,
  HELP,
  ERROR,
  NAVY,
  SUCCESS,
  WARNING,
  BASE,
  BORDER,
  TEAL,
} from "../../utils/constants/colors";
import { ICON_LABELS, fontSize, fontWeight } from "../../utils/constants/UI";
import useRegeneratingSteps from "../../hooks/useRegeneratingSteps";
import CustomHeader from "../../components/Header/CustomHeader";
import { STRINGS } from "./strings";
import { EMAIL_MARKETING_CAMPAIGN_PATH } from "../../utils/constants/routes";
import TypographyInline from "../../components/Typography/TypographyInline";
import EventEmitter from "../../utils/emitter/EventEmitter";
import AvatarName from "../../components/Avatar/AvatarName";
import SkeletonLoader from "../../components/Loaders/SkeletonLoader";
import IconComponent from "../../components/Icon/IconComponent";
import { Button } from "../../components/Buttons";
import classes from "./marketing.module.css";
import CustomModal from "../../components/Modals/CustomModal";
import SearchField from "../../components/InputFields/SearchField";
import {
  StatsDialog,
  ScheduleAutomatedCampaignModal,
} from "./components/Campaign";
import UserProfileDialog from "./components/Campaign/UserProfileDialog";
import ViewAiContentDialog from "./components/Campaign/ViewAiContentDialog";
import {
  NEW_LOGIN_REACHIQ_LOGO,
  NO_RECORD_UPDATE,
} from "../../utils/constants/assets";
import Image from "../../components/Image/Image";
import ICPAnalysisDialog from "./CampaignPipeline/ICPAnalysisDialog";
import TimezoneChip from "./CampaignPipeline/TimezoneChip";
import BounceSplitChip from "./components/BounceSplitChip";
import useLiveCampaignStats from "../../hooks/useLiveCampaignStats";

// Step type mapping
const STEP_TYPE_DETAILS = {
  1: { label: STRINGS.EMAIL, icon: ICON_LABELS.EMAIL },
  2: { label: STRINGS.TODO, icon: ICON_LABELS.TASK },
  3: { label: STRINGS.CALL, icon: ICON_LABELS.CALL },
};

// Inline Styles
const styles = {
  container1: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    background: BASE.BACKGROUND,
  },
  tabContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: `1px solid ${BORDER.LIGHT}`,
    padding: "0 24px",
    background: BASE.WHITE,
  },
  tabsWrapper: {
    display: "flex",
    gap: "24px",
  },
  tab: {
    padding: "12px 4px",
    cursor: "pointer",
    position: "relative",
    transition: "all 0.2s ease",
  },
  selectedTab: {
    padding: "12px 4px",
    cursor: "pointer",
    position: "relative",
    borderBottom: `2px solid ${BRAND.PRIMARY}`,
  },
  containAllStats: {
    display: "flex",
    gap: "12px",
    padding: "16px 24px",
    borderBottom: `1px solid ${BORDER.LIGHT}`,
    width: "100%",
  },
  eachContactStat: {
    flex: 1,
    minWidth: 0,
    padding: "16px 20px",
    background: BASE.WHITE,
    border: `1px solid ${BORDER.DEFAULT}`,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  bounceValueRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px 8px",
    minWidth: 0,
  },
  searchAndStatusContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    background: BASE.WHITE,
    borderBottom: `1px solid ${BORDER.LIGHT}`,
    gap: "16px",
  },
  commonWidthContact: {
    flex: "0 0 300px",
  },
  statusContainer: {
    display: "flex",
    gap: "20px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  statusDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
  },
  statusItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  contactsRootContainer: {
    height: "calc(100vh - 320px)",
    overflowY: "auto",
    background: BASE.BACKGROUND,
  },
  contactsContainer: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  containEachContact: {
    background: BASE.WHITE,
    border: `1px solid ${BORDER.LIGHT}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  eachContact: {
    padding: "16px 20px",
    borderBottom: `1px solid ${BORDER.LIGHT}`,
  },
  flexSpaceBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  pureFlex: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    cursor: "pointer",
  },
  alignSelfCenter: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  containStepList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "12px",
    padding: "16px 20px",
  },
  stepCardBase: {
    padding: "16px",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  stepCardSent: {
    borderLeft: `3px solid ${HELP.W_500}`,
    background: HELP.W_50,
  },
  stepCardDeliver: {
    borderLeft: `3px solid ${INTER_DIMENSIONAL_BLUE.W_500}`,
    background: INTER_DIMENSIONAL_BLUE.W_50,
  },
  stepCardBounce: {
    borderLeft: `3px solid ${ERROR.W_500}`,
    background: ERROR.W_50,
  },
  stepCardUnsubscribe: {
    borderLeft: `3px solid ${NAVY.W_500}`,
    background: NAVY.W_50,
  },
  stepCardLead: {
    borderLeft: `3px solid ${SUCCESS.W_700}`,
    background: SUCCESS.W_50,
  },
  stepCardOpened: {
    borderLeft: `3px solid ${TEAL.W_500}`,
    background: TEAL.W_100,
  },
  stepCardReplied: {
    borderLeft: `3px solid ${SUCCESS.W_300}`,
    background: "rgb(216 251 227)",
  },
  // MEETING (2026-08-31): prospect booked a meeting from this step —
  // strongest positive signal, purple so it stands apart from Replied/Lead
  stepCardMeeting: {
    borderLeft: `3px solid ${WARNING.W_500}`,
    background: WARNING.W_50,
  },
  stepCardActive: {
    border: `1px solid ${BORDER.DEFAULT}`,
    background: BASE.BACKGROUND_LIGHT,
  },
  // FAILED (2026-08-21): send permanently failed — was only visible in the
  // tooltip (card fell back to the Active style). Full red border so it can't
  // be mistaken for Bounced (left-accent only) or a pending step.
  stepCardFailed: {
    border: `1px solid ${ERROR.W_500}`,
    borderLeft: `3px solid ${ERROR.W_500}`,
    background: ERROR.W_50,
  },
  stepCardHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  },
  justifyBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px",
  },
  noRecordFoundContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "60px 20px",
    gap: "16px",
  },
  footerRightData: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
};

// Stats color mapping based on the image
const STATS_COLOR_MAP = {
  total_enrolled: GREY.PRIMARY,
  sent: HELP.W_500, // Blue
  deliver: INTER_DIMENSIONAL_BLUE.W_500, // Purple-blue
  lead: BRAND.PRIMARY, // Red
  bounce: GREY.PRIMARY,
  open: INTER_DIMENSIONAL_BLUE.W_500, // Purple
  meeting_book: BRAND.PRIMARY, // Orange/Red
  click: WARNING.W_500, // Orange
  reply: WARNING.W_500, // Orange
  unsubscribe: GREY.PRIMARY,
  total_qualified: SUCCESS.W_500,
  total_skipped: ERROR.W_500,
  threshold_percentage: WARNING.W_500,
};

const CampaignContactsScreen = () => {
  const CURRENT_TAB_ID = STRINGS.CAMPAIGN_CONTACTS_ID;
  const navigate = useNavigate();
  const params = useParams();
  const ids = params?.campaignId?.split("&");
  const campaignId = ids[0];
  // live "this step is regenerating" flags (Firebase) — survive page refresh
  const regenKeys = useRegeneratingSteps(campaignId);
  const sequenceId = ids[1];

  const [loading, setLoading] = useState(true);
  const stepsCount = 1;
  const [start, setStart] = useState(0);
  const [search, setSearch] = useState("");
  const [allRecordsFetched, setAllRecordsFetched] = useState(false);
  const [statsShowModalOpen, setStatsModalShowOpen] = useState(false);
  const [name, setName] = useState("");
  const [records, setRecords] = useState({});
  const [statsPayload, setStatsPayload] = useState({});
  const [totalCount, setTotalCount] = useState(-1);
  const [campaignStats, setCampaignStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);
  const [totalEnroled, setTotalEnroled] = useState("");
  const [camListId, setCamListId] = useState("");
  const [campaignStatus, setCampaignStatus] = useState("");
  const [isIcpCampaign, setIsIcpCampaign] = useState(false);
  const [totalQualified, setTotalQualified] = useState(0);
  const [totalSkipped, setTotalSkipped] = useState(0);
  const [icpThreshold, setIcpThreshold] = useState(0);
  const [showIcpModal, setShowIcpModal] = useState(false);
  const [selectedIcpContact, setSelectedIcpContact] = useState(null);
  const [showUnerollModal, setShowEnrollModal] = useState(false);
  const [unenrolling, setUnenrolling] = useState(false);
  const [email, setEmail] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [viewAiOpen, setViewAiOpen] = useState(false);
  const [interruptionReason, setInterruptionReason] = useState("");
  const [selectedItem, setSelectedItem] = useState({
    prospect: null,
    step: null,
    stepNumber: null,
    email: "",
  });

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [sequenceType, setSequenceType] = useState("");
  const [campaignTimezone, setCampaignTimezone] = useState("");
  const [campaignScheduledDate, setCampaignScheduledDate] = useState(null);
  const [pauseResumeLoading, setPauseResumeLoading] = useState(false);

  const [headerItems, setHeaderItems] = useState([
    {
      id: STRINGS.TYPE_CAMP,
      label: STRINGS.CAMPAIGN_TEXT,
      path: EMAIL_MARKETING_CAMPAIGN_PATH,
    },
  ]);

  const [activeSubTab, setActiveSubTab] = useState(
    STRINGS.CAMPAIGN_CONTACTS_ID,
  );

  const EMAIL_MARKETING_TABS = useMemo(
    () => [
      {
        label: STRINGS.CAMPAIGN_CONTACTS_LABEL,
        id: STRINGS.CAMPAIGN_CONTACTS_ID,
      },
      ...(isIcpCampaign
        ? [
            {
              label: STRINGS.SKIPPED_CONTACTS_LABEL,
              id: STRINGS.SKIPPED_CONTACTS_ID,
            },
          ]
        : []),
      ...(sequenceType !== "tenantPrompt"
        ? [
            {
              label: STRINGS.CAMPAIGN_OVERVIEW_LABEL,
              id: STRINGS.CAMPAIGN_OVERVIEW_ID,
            },
          ]
        : []),
      {
        label: STRINGS.CAMPAIGN_ACTIVITY_LABEL,
        id: STRINGS.CAMPAIGN_ACTIVITY_ID,
      },
      {
        label: STRINGS.CAMPAIGN_SETTINGS_LABEL,
        id: STRINGS.CAMPAIGN_SETTINGS_ID,
      },
    ],
    [sequenceType, isIcpCampaign],
  );

  const isReadyToRun =
    campaignStatus === "Ready to Run" || campaignStatus === "Ready To Run";

  const needsScheduling = useMemo(() => {
    if (!isReadyToRun) return false;
    return Object.values(records).some((contact) =>
      contact.steps?.some(
        (step) => !step.processing_date_time && !step.show_date_time,
      ),
    );
  }, [records, isReadyToRun]);

  const generateName = (val) => {
    const data = val.includes("undefined") ? " " : val;
    return data;
  };

  const handleNavigate = (item) => {
    if (
      item.id === STRINGS.CAMPAIGN_CONTACTS_ID ||
      item.id === STRINGS.SKIPPED_CONTACTS_ID
    ) {
      setActiveSubTab(item.id);
      return;
    }
    try {
      navigate(
        `${EMAIL_MARKETING_CAMPAIGN_PATH}/${params?.campaignId}/${item?.id}`,
      );
    } catch (error) {
      Logger(error);
    }
  };

  const handleViewAiContent = (prospectData, stepData, stepNumber) => {
    setSelectedItem({
      prospect: prospectData,
      step: stepData,
      stepNumber: stepNumber,
      email: prospectData?.email,
    });
    setViewAiOpen(true);
  };

  const handleContactUnenroll = (email) => {
    setShowEnrollModal(!showUnerollModal);
    setEmail(email);
  };

  const handleProcessingPopupClose = () => {
    navigate(EMAIL_MARKETING_CAMPAIGN_PATH);
  };

  const handleScroll = async (e) => {
    if (
      e?.currentTarget.offsetHeight + e?.currentTarget.scrollTop >=
      e?.currentTarget.scrollHeight - 10
    ) {
      if (!loading && !allRecordsFetched) {
        try {
          setLoading(true);
          const standardPayload = {
            campaign_id: campaignId,
            size: 5 * stepsCount,
            page: start + 1,
            cam_list_id: camListId,
          };

          const promises = [
            postRequest(
              navigate,
              campaignEndpoints.campaignContactList,
              standardPayload,
            ),
            ...(isIcpCampaign
              ? [
                  postRequest(
                    navigate,
                    campaignEndpoints.icpQualifiedContacts,
                    {
                      campaign_id: campaignId,
                      page: start + 1,
                      limit: 5 * stepsCount,
                    },
                  ),
                ]
              : []),
          ];

          const results = await Promise.allSettled(promises);
          const standardRes =
            results[0]?.status === "fulfilled" ? results[0].value : null;
          const icpScrollRes =
            isIcpCampaign && results[1]?.status === "fulfilled"
              ? results[1].value
              : null;

          setStart(start + 1);

          let newRecords = standardRes?.data?.data || {};

          // Merge ICP scores into new records
          if (icpScrollRes) {
            const icpData = icpScrollRes?.data?.data;
            const qualifiedArr = icpData?.qualified?.contacts || [];
            const skippedArr =
              icpData?.skipped?.total_contacts ||
              icpData?.skipped?.contacts ||
              [];

            qualifiedArr.forEach((c) => {
              if (newRecords[c.email]) {
                newRecords[c.email] = {
                  ...newRecords[c.email],
                  ...c,
                  icp_qualified: true,
                };
              }
            });

            skippedArr.forEach((c) => {
              if (newRecords[c.email]) {
                newRecords[c.email] = {
                  ...newRecords[c.email],
                  ...c,
                  icp_qualified: false,
                };
              }
            });
          }

          const updatedRecords = { ...records, ...newRecords };
          setRecords(updatedRecords);

          if (Object.keys(updatedRecords).length >= totalCount) {
            setAllRecordsFetched(true);
          }

          if (!Object.keys(newRecords).length) {
            setAllRecordsFetched(true);
          }
        } catch (error) {
          setAllRecordsFetched(true);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const unenrollContact = async (e) => {
    try {
      setUnenrolling(true);
      const response = await postRequest(navigate, campaignEndpoints.UNENROLL, {
        email: email,
        campaign_id: campaignId,
      });
      const new_records = { ...records };
      delete new_records[email];
      setRecords(new_records);
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
    } catch (error) {
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
    } finally {
      setUnenrolling(false);
      setShowEnrollModal(false);
    }
  };

  const STATUS_LIST = [
    // dotColor = the step card's background so the legend maps 1:1 to card colors;
    // dotBorder = that status's accent so pale dots stay visible on white.
    { label: "Sent", color: HELP.W_500, dotColor: HELP.W_50, dotBorder: HELP.W_500 },
    {
      label: "Delivered",
      color: INTER_DIMENSIONAL_BLUE.W_500,
      dotColor: INTER_DIMENSIONAL_BLUE.W_50,
      dotBorder: INTER_DIMENSIONAL_BLUE.W_500,
    },
    { label: "Bounced", color: ERROR.W_500, dotColor: ERROR.W_50, dotBorder: ERROR.W_500 },
    { label: "Failed", color: ERROR.W_700, dotColor: ERROR.W_50, dotBorder: ERROR.W_700 },
    { label: "Unsubscribed", color: NAVY.W_500, dotColor: NAVY.W_50, dotBorder: NAVY.W_500 },
    { label: "Opened", color: TEAL.W_500, dotColor: TEAL.W_100, dotBorder: TEAL.W_500 },
    { label: "Replied", color: SUCCESS.W_300, dotColor: "rgb(216 251 227)", dotBorder: "rgb(40 171 84)" },
    { label: "Lead", color: SUCCESS.W_700, dotColor: SUCCESS.W_50, dotBorder: SUCCESS.W_700 },
    { label: "Meeting", color: WARNING.W_600, dotColor: WARNING.W_50, dotBorder: WARNING.W_600 },
  ];

  const STATUS_WISE_STYLES = {
    Sent: { ...styles.stepCardBase, ...styles.stepCardSent },
    Bounced: { ...styles.stepCardBase, ...styles.stepCardBounce },
    Lead: { ...styles.stepCardBase, ...styles.stepCardLead },
    Opened: { ...styles.stepCardBase, ...styles.stepCardOpened },
    Replied: { ...styles.stepCardBase, ...styles.stepCardReplied },
    Delivered: { ...styles.stepCardBase, ...styles.stepCardDeliver },
    Active: { ...styles.stepCardBase, ...styles.stepCardActive },
    Unsubscribed: { ...styles.stepCardBase, ...styles.stepCardUnsubscribe },
    Failed: { ...styles.stepCardBase, ...styles.stepCardFailed },
    Meeting: { ...styles.stepCardBase, ...styles.stepCardMeeting },
  };

  // The API returns statuses in inconsistent casing (e.g. "sent", "QUEUED",
  // "active") while our style/icon maps are keyed by capitalized names.
  // Normalize to a single canonical capitalized form so matching is
  // case-insensitive across all status types.
  const normalizeStatus = (status) => {
    if (!status || typeof status !== "string") return "";
    const lower = status.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  const modalHeaderData = {
    heading: {
      id: STRINGS.UNENROLL,
      label: STRINGS.UNENROLL,
    },
  };

  const modalFooterRightData = (
    <div style={styles.footerRightData}>
      <Box>
        <Button
          label={STRINGS.CANCEL}
          variant={"ghost"}
          size={"sm36"}
          onClick={handleContactUnenroll}
        />
      </Box>
      <Box>
        <Button
          label={STRINGS.UNENROLL}
          size={"sm36"}
          variant={"primary"}
          onClick={unenrollContact}
          isLoading={unenrolling}
          disabled={campaignStatus === "completed"}
        />
      </Box>
    </div>
  );

  const opnStatsDialog = (key) => {
    setStatsModalShowOpen(true);
    setStatsPayload({
      head: { id: key },
      row: {
        _id: campaignId,
        cam_sequence_id: sequenceId,
        name: name,
      },
    });
  };

  const showProfileHandle = (email) => {
    setUserEmail(email);
    setOpenProfileDialog(true);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    try {
      setSearch(e.target.value);
      const payload = {
        campaign_id: campaignId,
        size: 5,
        page: 0,
        cam_list_id: camListId,
        name: e.target.value,
      };

      const promises = [
        postRequest(navigate, campaignEndpoints.campaignContactList, payload),
        ...(isIcpCampaign
          ? [
              postRequest(navigate, campaignEndpoints.icpQualifiedContacts, {
                campaign_id: campaignId,
                page: 0,
                limit: 5,
              }),
            ]
          : []),
      ];

      const results = await Promise.allSettled(promises);
      const steps_response =
        results[0]?.status === "fulfilled" ? results[0].value : null;
      const icpSearchRes =
        isIcpCampaign && results[1]?.status === "fulfilled"
          ? results[1].value
          : null;

      setTotalCount(steps_response?.data?.count || 0);
      let searchRecords = steps_response?.data?.data || {};

      if (icpSearchRes) {
        const icpData = icpSearchRes?.data?.data;
        const qualifiedArr = icpData?.qualified?.contacts || [];
        const skippedArr =
          icpData?.skipped?.total_contacts || icpData?.skipped?.contacts || [];

        qualifiedArr.forEach((c) => {
          if (searchRecords[c.email]) {
            searchRecords[c.email] = {
              ...searchRecords[c.email],
              ...c,
              icp_qualified: true,
            };
          }
        });

        skippedArr.forEach((c) => {
          if (searchRecords[c.email]) {
            searchRecords[c.email] = {
              ...searchRecords[c.email],
              ...c,
              icp_qualified: false,
            };
          }
        });
      }

      setRecords(searchRecords);
    } catch (e) {}
  };
  const debounceSearch = debounceHelper(handleSearch, DEBOUNCE_DELAY);

  const fetchData = async () => {
    try {
      setLoading(true);
      const campaign_details = await getRequest(
        navigate,
        campaignEndpoints.eachCampaignDetail + "/" + campaignId,
      );
      const cam_list_id = campaign_details?.data?.data?.cam_list_id;
      setCamListId(cam_list_id);
      setName(campaign_details?.data?.data?.name);

      const currentState = campaign_details?.data?.data?.state;
      setCampaignStatus(currentState);

      const isAlreadyShown = localStorage.getItem(
        `reachiq_campaign_ready_shown_${campaignId}`,
      );

      const seqType = campaign_details?.data?.data?.sequence_type;
      setSequenceType(seqType);

      setCampaignTimezone(campaign_details?.data?.data?.timezone || "");
      setCampaignScheduledDate(
        campaign_details?.data?.data?.scheduled_date || null,
      );

      const isIcp =
        campaign_details?.data?.data?.isIcpCampaign ||
        seqType === "tenantPrompt";
      setIsIcpCampaign(isIcp);

      const reason =
        campaign_details?.data?.data?.interruption_reason ||
        campaign_details?.data?.data?.reason;
      setInterruptionReason(reason);

      const statusNormalized =
        currentState?.toLowerCase()?.replace(/ /g, "_") || "";
      const isInterrupted = statusNormalized === "interrupted";

      const isProcessingManual =
        seqType === "manual" &&
        ["entry", "setting_up"].includes(statusNormalized);
      const isProcessingAuto =
        seqType === "automated" &&
        [
          "setting_up",
          "analyzing_data",
          "personalizing",
          "personalizing_sequence",
          "ai_generating_response",
          "ai_processing",
        ].includes(statusNormalized);

      if (isInterrupted) {
        EventEmitter.emit(
          EMITTER_ERROR,
          `Your campaign got interrupted. ${reason || "Please check your settings or contact support to resume."}`,
        );
        navigate(EMAIL_MARKETING_CAMPAIGN_PATH);
        return;
      }

      if (isProcessingManual || isProcessingAuto) {
        EventEmitter.emit(EMITTER_ERROR, "Campaigns creation under process");
        navigate(EMAIL_MARKETING_CAMPAIGN_PATH);
        return;
      }

      const isReadyToRun =
        currentState === "Ready to Run" || currentState === "Ready To Run";

      if (
        isReadyToRun &&
        (seqType === "automated" || seqType === "tenantPrompt") &&
        !isAlreadyShown
      ) {
        setShowScheduleModal(true);
      }

      setHeaderItems([
        {
          id: STRINGS.TYPE_CAMP,
          label: STRINGS.CAMPAIGN_TEXT,
          path: EMAIL_MARKETING_CAMPAIGN_PATH,
        },
        {
          id: campaign_details?.data?.data?._id,
          label: campaign_details?.data?.data?.name,
        },
      ]);
      setTotalEnroled(campaign_details?.data?.data?.total_enrolled);

      const standardPayload = {
        campaign_id: campaignId,
        size: 5,
        page: 0,
        cam_list_id: cam_list_id,
      };

      // Both APIs must use the same page & size/limit so ICP scores
      // are always available for every displayed contact
      const icpPayload = {
        campaign_id: campaignId,
        page: 0,
        limit: 5,
      };

      const promises = [
        postRequest(
          navigate,
          campaignEndpoints.campaignContactList,
          standardPayload,
        ),
        ...(isIcp
          ? [
              postRequest(
                navigate,
                campaignEndpoints.icpQualifiedContacts,
                icpPayload,
              ),
            ]
          : []),
      ];

      const results = await Promise.allSettled(promises);
      const standardRes =
        results[0]?.status === "fulfilled" ? results[0].value : null;
      const icpRes =
        isIcp && results[1]?.status === "fulfilled" ? results[1].value : null;

      let fetchedRecords = standardRes?.data?.data || {};

      if (isIcp && icpRes) {
        const icpData = icpRes?.data?.data;
        const totalQ = icpData?.qualified?.total || 0;
        const totalS = icpData?.skipped?.total || 0;
        setTotalQualified(totalQ);
        setTotalSkipped(totalS);
        setTotalCount(icpData?.total_verified || 0);
        setIcpThreshold(icpData?.threshold_percentage || 0);

        const qualifiedArr = icpData?.qualified?.contacts || [];
        const skippedArr =
          icpData?.skipped?.total_contacts || icpData?.skipped?.contacts || [];

        qualifiedArr.forEach((c) => {
          const email = c.email;
          if (fetchedRecords[email]) {
            fetchedRecords[email] = {
              ...fetchedRecords[email],
              ...c,
              icp_qualified: true,
            };
          } else {
            fetchedRecords[email] = {
              ...c,
              full_name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
              icp_qualified: true,
            };
          }
        });

        skippedArr.forEach((c) => {
          const email = c.email;
          if (fetchedRecords[email]) {
            fetchedRecords[email] = {
              ...fetchedRecords[email],
              ...c,
              icp_qualified: false,
            };
          } else {
            fetchedRecords[email] = {
              ...c,
              full_name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
              icp_qualified: false,
            };
          }
        });
      } else {
        setTotalCount(standardRes?.data?.count || 0);
      }

      setRecords(fetchedRecords);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
    }

    try {
      setStatsLoading(true);
      const campaign_stats = await postRequest(
        navigate,
        campaignEndpoints.campStats,
        { campaign_ids: [campaignId] },
      );
      setCampaignStats(
        processStatsKeyValuePairs(
          campaign_stats?.data?.records?.[campaignId] || {},
        ),
      );
    } catch (error) {
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
    } finally {
      setStatsLoading(false);
    }
  };

  const handlePauseResume = async (action) => {
    try {
      setPauseResumeLoading(true);
      const endpoint =
        action === "pause"
          ? campaignEndpoints.puaseCampaign
          : campaignEndpoints.resumeCampaign;
      const resp = await getRequest(navigate, `${endpoint}/${campaignId}`);
      EventEmitter.emit(
        EMITTER_SUCCESS,
        resp?.data?.message ||
          (action === "pause" ? "Campaign paused" : "Campaign resumed"),
      );
      fetchData();
    } catch (error) {
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message ||
          `Failed to ${action === "pause" ? "pause" : "resume"} campaign`,
      );
    } finally {
      setPauseResumeLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [navigate, campaignId, sequenceId]);

  // Live send/open counters pushed by the backend into Firebase RTDB.
  // The stats API above stays the baseline (and re-seeds the node server-side);
  // per field we display max(baseline, live) so the counter never drops when
  // the node is absent or cleaned up after campaign completion.
  const liveStats = useLiveCampaignStats(campaignId);
  const displayedStats = useMemo(() => {
    if (!liveStats) return campaignStats;
    return {
      ...campaignStats,
      sent: Math.max(campaignStats?.sent || 0, liveStats?.sent || 0),
      open: Math.max(campaignStats?.open || 0, liveStats?.open || 0),
    };
  }, [campaignStats, liveStats]);

  const displayStatsArr = useMemo(() => {
    const stats = [...CONTACTS_STATS_TYPE];
    if (isIcpCampaign) {
      stats.splice(
        1,
        0,
        { key: "total_qualified", label: "Qualified" },
        { key: "total_skipped", label: "Skipped" },
        { key: "threshold_percentage", label: "Threshold" },
      );
    }
    return stats;
  }, [isIcpCampaign]);

  useEffect(() => {
    const scrollContainer = document.getElementById("modal_body");
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
    // When switching to Qualified tab, we might need to fetch more if list is large
    // When switching to Skipped tab, we have everything from the initial ICP fetch
    if (activeSubTab === STRINGS.SKIPPED_CONTACTS_ID) {
      setAllRecordsFetched(true);
    } else {
      // Check if we already have all standard records
      if (Object.keys(records).length < totalCount) {
        setAllRecordsFetched(false);
      }
    }
  }, [activeSubTab, totalCount]);

  const filteredRecordKeys = useMemo(() => {
    const keys = Object.keys(records);
    if (!isIcpCampaign) return keys;

    if (activeSubTab === STRINGS.CAMPAIGN_CONTACTS_ID) {
      return keys.filter((k) => records[k].icp_qualified !== false);
    }
    if (activeSubTab === STRINGS.SKIPPED_CONTACTS_ID) {
      return keys.filter((k) => records[k].icp_qualified === false);
    }
    return keys;
  }, [records, isIcpCampaign, activeSubTab]);

  return (
    <Box sx={styles.container1}>
      <CustomHeader items={headerItems} />
      <Box sx={styles.tabContainer}>
        <Box sx={styles.tabsWrapper}>
          {EMAIL_MARKETING_TABS?.map((item) => {
            return (
              <Box
                key={item.id}
                sx={activeSubTab === item.id ? styles.selectedTab : styles.tab}
                onClick={() => handleNavigate(item)}
              >
                <TypographyInline
                  size={"sm"}
                  color={
                    activeSubTab === item.id ? BRAND.PRIMARY : GREY.SECONDARY
                  }
                  fontWeight={
                    activeSubTab === item.id
                      ? fontWeight.SEMIBOLD
                      : fontWeight.REGULAR
                  }
                  label={item.label}
                />
              </Box>
            );
          })}
        </Box>
        {(sequenceType === "automated" || sequenceType === "tenantPrompt") && (
          <Box
            sx={{
              paddingRight: "4px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <TimezoneChip
              timezone={campaignTimezone}
              scheduledDate={campaignScheduledDate}
            />
            {(() => {
              const status = (campaignStatus || "")
                .toLowerCase()
                .replace(/ /g, "_");
              const isPausedState = status === "paused";
              const isRunningState =
                status === "scheduled" ||
                status === "running" ||
                status === "sending";

              if (isPausedState) {
                return (
                  <Button
                    label="Resume Campaign"
                    size="sm36"
                    variant="primary"
                    isLoading={pauseResumeLoading}
                    onClick={() => handlePauseResume("resume")}
                  />
                );
              }
              if (isRunningState) {
                return (
                  <Button
                    label="Pause Campaign"
                    size="sm36"
                    variant="ghost"
                    isLoading={pauseResumeLoading}
                    onClick={() => handlePauseResume("pause")}
                  />
                );
              }
              return (
                <Button
                  label="Schedule Campaign"
                  size="sm36"
                  variant="primary"
                  disabled={!needsScheduling}
                  onClick={() => setShowScheduleModal(true)}
                />
              );
            })()}
          </Box>
        )}
      </Box>

      <Box sx={styles.containAllStats}>
        {displayStatsArr.map((eachStat, index) => {
          const statColor = STATS_COLOR_MAP[eachStat.key] || GREY.PRIMARY;

          return (
            <Box
              key={eachStat.key}
              sx={{
                ...styles.eachContactStat,
                "&:hover": {
                  borderColor: statColor,
                  boxShadow: `0 2px 8px ${statColor}33`,
                },
              }}
              onClick={() => {
                const nonClickableKeys = [
                  "total_enrolled",
                  "total_qualified",
                  "total_skipped",
                  "threshold_percentage",
                ];
                if (!nonClickableKeys.includes(eachStat.key)) {
                  opnStatsDialog(eachStat.key);
                }
              }}
            >
              {statsLoading ? (
                <Box>
                  <SkeletonLoader
                    variant="text"
                    skeletonWidth="40px"
                    skeletonHeight="28px"
                  />
                </Box>
              ) : eachStat?.key === "total_enrolled" ? (
                <TypographyInline
                  size={"xl"}
                  color={statColor}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={totalEnroled}
                />
              ) : eachStat?.key === "total_qualified" ? (
                <TypographyInline
                  size={"xl"}
                  color={statColor}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={totalQualified}
                />
              ) : eachStat?.key === "total_skipped" ? (
                <TypographyInline
                  size={"xl"}
                  color={statColor}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={totalSkipped}
                />
              ) : eachStat?.key === "threshold_percentage" ? (
                <TypographyInline
                  size={"xl"}
                  color={statColor}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={`${icpThreshold}%`}
                />
              ) : eachStat?.key === "bounce" &&
                displayedStats?.bounce_hard != null ? (
                <Box sx={styles.bounceValueRow}>
                  <TypographyInline
                    size={"xl"}
                    color={statColor}
                    fontWeight={fontWeight.SEMIBOLD}
                    label={displayedStats?.[eachStat?.key] || 0}
                  />
                  <BounceSplitChip
                    hard={displayedStats?.bounce_hard || 0}
                    soft={displayedStats?.bounce_soft || 0}
                  />
                </Box>
              ) : (
                <TypographyInline
                  size={"xl"}
                  color={statColor}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={displayedStats?.[eachStat?.key] || 0}
                />
              )}
              <TypographyInline
                size={"sm"}
                color={GREY.SECONDARY}
                fontWeight={fontWeight.REGULAR}
                label={`Total ${eachStat.label}`}
              />
            </Box>
          );
        })}
      </Box>

      <Box
        onScroll={handleScroll}
        id="modal_body"
        sx={{ ...styles.contactsRootContainer, display: "block" }}
      >
        <Box sx={styles.searchAndStatusContainer}>
          <Box sx={styles.commonWidthContact}>
            <SearchField
              autoComplete="off"
              size={"sm36"}
              placeholder={STRINGS.SEARCH_CONTACTS}
              onChange={debounceSearch}
            />
          </Box>
          <Box sx={styles.statusContainer}>
            {STATUS_LIST?.map((item, idx) => {
              return (
                <Box key={idx} sx={styles.statusItem}>
                  <Box
                    sx={{
                      ...styles.statusDot,
                      backgroundColor: item.dotColor,
                      border: item.dotBorder
                        ? `1.5px solid ${item.dotBorder}`
                        : undefined,
                    }}
                  />
                  <TypographyInline
                    size={"sm"}
                    color={GREY.SECONDARY}
                    fontWeight={fontWeight.REGULAR}
                    label={item.label}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box sx={styles.contactsContainer}>
          {filteredRecordKeys?.length ? (
            <>
              {filteredRecordKeys.map((recordKey) => {
                return (
                  <Box sx={styles.containEachContact} key={recordKey}>
                    <Box sx={styles.eachContact}>
                      <Box>
                        <Box sx={styles.flexSpaceBetween}>
                          <Box
                            sx={styles.pureFlex}
                            onClick={() =>
                              showProfileHandle(records[recordKey].email)
                            }
                          >
                            <AvatarName
                              size={"small"}
                              name={
                                records[recordKey].full_name ||
                                generateNameFromEmail(records[recordKey].email)
                              }
                            />
                            <Tooltip title={records[recordKey].email}>
                              <Box sx={styles.alignSelfCenter}>
                                <TypographyInline
                                  size={"sm"}
                                  color={GREY.PRIMARY}
                                  fontWeight={fontWeight.SEMIBOLD}
                                  label={generateName(
                                    records[recordKey].full_name,
                                  )}
                                />
                                <TypographyInline
                                  size={"sm"}
                                  color={GREY.SECONDARY}
                                  fontWeight={fontWeight.REGULAR}
                                  label={
                                    !records[recordKey].company_name
                                      ? `Email: ${records[recordKey].email}`
                                      : `Company: ${records[recordKey].company_name}`
                                  }
                                />
                              </Box>
                            </Tooltip>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              gap: "12px",
                              alignItems: "center",
                            }}
                          >
                            {isIcpCampaign &&
                              records[recordKey].company_percentage !==
                                undefined && (
                                <Box
                                  sx={{
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                    background:
                                      records[recordKey].company_percentage >=
                                      icpThreshold
                                        ? SUCCESS.W_50
                                        : ERROR.W_50,
                                    border: `1px solid ${
                                      records[recordKey].company_percentage >=
                                      icpThreshold
                                        ? "#bbf7d0"
                                        : "#fecaca"
                                    }`,
                                    transition: "opacity 0.15s ease",
                                    "&:hover": { opacity: 0.75 },
                                  }}
                                  onClick={() => {
                                    setSelectedIcpContact(records[recordKey]);
                                    setShowIcpModal(true);
                                  }}
                                >
                                  <TypographyInline
                                    label={`ICP: ${records[recordKey].company_percentage}%`}
                                    size="sm"
                                    color={
                                      records[recordKey].company_percentage >=
                                      icpThreshold
                                        ? SUCCESS.W_500
                                        : ERROR.W_500
                                    }
                                    fontWeight={fontWeight.SEMIBOLD}
                                  />
                                </Box>
                              )}
                            <Box>
                              <Button
                                label={STRINGS.UNENROLL}
                                size={"sm36"}
                                variant={"ghost"}
                                onClick={() =>
                                  handleContactUnenroll(
                                    records[recordKey].email,
                                  )
                                }
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={styles.containStepList}>
                      {(records?.[recordKey]?.steps || [])?.map(
                        (eachRecord, index1) => {
                          const dateTimeString =
                            eachRecord?.processing_date_time;
                          const dateTime = new Date(
                            dateTimeString,
                          ).toLocaleString();

                          const currentStepNumber =
                            Number(eachRecord?.step_number) || index1 + 1;

                          const normalizedStatus = normalizeStatus(
                            eachRecord?.status,
                          );

                          return (
                            <Tooltip
                              key={index1}
                              title={
                                normalizedStatus === "Meeting"
                                  ? "Meeting booked"
                                  : capitalizeFirstLetter(eachRecord?.status)
                              }
                            >
                              <Box
                                sx={{
                                  ...(STATUS_WISE_STYLES[normalizedStatus] ||
                                    STATUS_WISE_STYLES.Active),
                                  "&:hover": styles.stepCardHover,
                                }}
                                onClick={() =>
                                  handleViewAiContent(
                                    records[recordKey],
                                    eachRecord,
                                    currentStepNumber,
                                  )
                                }
                              >
                                <Box>
                                  <Box sx={styles.justifyBetween}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      <TypographyInline
                                        size={"sm"}
                                        color={GREY.PRIMARY}
                                        fontWeight={fontWeight.SEMIBOLD}
                                        label={`Step ${currentStepNumber}`}
                                      />
                                      {eachRecord?.step_type &&
                                        STEP_TYPE_DETAILS[
                                          eachRecord.step_type
                                        ] && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "4px",
                                              background: BASE.BACKGROUND,
                                              padding: "2px 6px",
                                              borderRadius: "4px",
                                            }}
                                          >
                                            <IconComponent
                                              iconLabel={
                                                STEP_TYPE_DETAILS[
                                                  eachRecord.step_type
                                                ].icon
                                              }
                                              color={GREY.SECONDARY}
                                              fontSize={fontSize.XS}
                                            />
                                            <TypographyInline
                                              size={"micro"}
                                              color={GREY.SECONDARY}
                                              fontWeight={fontWeight.MEDIUM}
                                              label={
                                                STEP_TYPE_DETAILS[
                                                  eachRecord.step_type
                                                ].label
                                              }
                                            />
                                          </Box>
                                        )}
                                    </Box>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      {normalizedStatus === "Sent" && (
                                        <IconComponent
                                          iconLabel={ICON_LABELS.DONE}
                                          color={HELP.W_500}
                                          fontSize={fontSize.LG}
                                        />
                                      )}
                                      {normalizedStatus === "Delivered" && (
                                        <IconComponent
                                          iconLabel={ICON_LABELS.DONE_ALL}
                                          color={INTER_DIMENSIONAL_BLUE.W_500}
                                          fontSize={fontSize.LG}
                                        />
                                      )}
                                      {normalizedStatus === "Bounced" && (
                                        <IconComponent
                                          iconLabel={ICON_LABELS.ERROR}
                                          color={ERROR.W_500}
                                          fontSize={fontSize.LG}
                                        />
                                      )}
                                      {normalizedStatus === "Unsubscribed" && (
                                        <div
                                          style={{
                                            position: "relative",
                                            display: "inline-flex",
                                          }}
                                        >
                                          <IconComponent
                                            iconLabel={ICON_LABELS.EMAIL}
                                            color={NAVY.W_500}
                                            fontSize={fontSize.LG}
                                          />
                                          <div
                                            style={{
                                              position: "absolute",
                                              top: "-4px",
                                              right: "-5px",
                                              background: "#fff",
                                              borderRadius: "50%",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              lineHeight: 0,
                                            }}
                                          >
                                            <IconComponent
                                              iconLabel={ICON_LABELS.CLOSE}
                                              color={ERROR.W_500}
                                              fontSize={"11px"}
                                            />
                                          </div>
                                        </div>
                                      )}
                                      {normalizedStatus === "Opened" && (
                                        <IconComponent
                                          iconLabel={ICON_LABELS.VISIBILITY_ON}
                                          color={TEAL.W_500}
                                          fontSize={fontSize.LG}
                                        />
                                      )}
                                      {normalizedStatus === "Replied" && (
                                        <IconComponent
                                          iconLabel={ICON_LABELS.REPLY}
                                          color={"rgb(40 171 84)"}
                                          fontSize={fontSize.LG}
                                        />
                                      )}
                                      {normalizedStatus === "Lead" && (
                                        <IconComponent
                                          iconLabel={ICON_LABELS.CHECK_CIRCLE}
                                          color={SUCCESS.W_700}
                                          fontSize={fontSize.LG}
                                        />
                                      )}
                                      {normalizedStatus === "Meeting" && (
                                        <IconComponent
                                          iconLabel={ICON_LABELS.EVENT}
                                          color={WARNING.W_600}
                                          fontSize={fontSize.LG}
                                        />
                                      )}
                                    </Box>
                                  </Box>

                                  <div style={{ marginTop: "4px" }}>
                                    {/* REGENERATE: while Firebase says this
                                        contact+step is regenerating, the card
                                        says so — survives refresh/close */}
                                    {eachRecord?.cam_step_id &&
                                    regenKeys.has(
                                      `${eachRecord.cam_step_id}|${String(records?.[recordKey]?.email || "").toLowerCase()}`,
                                    ) ? (
                                      <TypographyInline
                                        size={"xs"}
                                        color={BRAND.PRIMARY}
                                        fontWeight={fontWeight.MEDIUM}
                                        label={"Regenerating…"}
                                      />
                                    ) : (
                                      <TypographyInline
                                        size={"xs"}
                                        color={GREY.TERTIARY}
                                        fontWeight={fontWeight.REGULAR}
                                        label={
                                          eachRecord?.show_date_time ||
                                          (eachRecord?.processing_date_time
                                            ? dateTime
                                            : "Waiting for scheduling to be assigned")
                                        }
                                      />
                                    )}
                                  </div>
                                  {eachRecord?.from_email &&
                                  (eachRecord?.step_type === 1 ||
                                    !eachRecord?.step_type) &&
                                  [
                                    "Sent",
                                    "Delivered",
                                    "Opened",
                                    "Replied",
                                    "Bounced",
                                    "Unsubscribed",
                                    "Lead",
                                    "Meeting",
                                  ].includes(normalizedStatus) ? (
                                    <Tooltip
                                      title={`From: ${eachRecord.from_email}`}
                                    >
                                      <div
                                        style={{
                                          marginTop: "2px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "4px",
                                          maxWidth: "220px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div style={{ flexShrink: 0, display: "flex" }}>
                                          <IconComponent
                                            iconLabel={ICON_LABELS.SEND}
                                            color={GREY.TERTIARY}
                                            fontSize={fontSize.XS}
                                          />
                                        </div>
                                        <div
                                          style={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <TypographyInline
                                            size={"xs"}
                                            color={GREY.TERTIARY}
                                            fontWeight={fontWeight.REGULAR}
                                            isEllipses={true}
                                            label={eachRecord.from_email}
                                          />
                                        </div>
                                      </div>
                                    </Tooltip>
                                  ) : null}
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        },
                      )}
                    </Box>
                  </Box>
                );
              })}
            </>
          ) : search?.length ? (
            <>
              <div style={styles.noRecordFoundContainer}>
                <div>
                  <Image
                    style={{ maxWidth: "200px", height: "auto" }}
                    src={NO_RECORD_UPDATE}
                  />
                </div>
                <TypographyInline
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.SEMIBOLD}
                  label={STRINGS.NO_RECORD_FOUND}
                  size={"xl"}
                />
              </div>
            </>
          ) : null}
          {loading ? (
            <Box sx={styles.loaderContainer}>
              <CircularLoader />
            </Box>
          ) : null}
        </Box>

        {showUnerollModal && (
          <CustomModal
            variant={"error"}
            open={true}
            close={handleContactUnenroll}
            header={modalHeaderData}
            footer={{ right: modalFooterRightData }}
          >
            <TypographyInline
              label={STRINGS.UNENROLL_CONFIRMATION}
              size={"sm"}
              fontWeight={fontWeight.REGULAR}
              color={GREY.SECONDARY}
            />
          </CustomModal>
        )}
      </Box>
      {statsShowModalOpen && (
        <StatsDialog
          open={statsShowModalOpen}
          setOpen={setStatsModalShowOpen}
          statsPayload={statsPayload}
          showProfileHandle={showProfileHandle}
        />
      )}
      {openProfileDialog && (
        <UserProfileDialog
          open={openProfileDialog}
          setOpen={setOpenProfileDialog}
          email={userEmail}
          campaignId={campaignId}
        />
      )}

      <ViewAiContentDialog
        open={viewAiOpen}
        onClose={() => setViewAiOpen(false)}
        prospect={selectedItem.prospect}
        step={selectedItem.step}
        stepNumber={selectedItem.stepNumber}
        email={selectedItem.email}
        campaignId={campaignId}
        sequenceId={sequenceId}
        navigate={navigate}
      />

      {showScheduleModal && (
        <ScheduleAutomatedCampaignModal
          open={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          campaignId={campaignId}
          campaignName={name}
          onRefresh={fetchData}
        />
      )}
      {showIcpModal && (
        <ICPAnalysisDialog
          open={showIcpModal}
          onClose={() => setShowIcpModal(false)}
          contact={
            selectedIcpContact
              ? {
                  name:
                    selectedIcpContact.full_name || selectedIcpContact.email,
                  company_score: selectedIcpContact.company_percentage || 0,
                  company_summary: selectedIcpContact.company_summary,
                  company_criteria_results: Object.entries(
                    selectedIcpContact.company_scores || {},
                  ).map(([key, val]) => ({
                    criterion: key,
                    score: val.score || 0,
                    matched: val.matched || false,
                    weight: val.weight || "nice-to-have",
                    reason: val.reason || "",
                    research: val.research || null,
                  })),
                }
              : null
          }
        />
      )}
    </Box>
  );
};

export default CampaignContactsScreen;
